import { formatGr } from '@odhaczka/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { monthLabel, monthlySpend, monthsBack, useMonthItems, type MonthSpend } from '../../data/spending';
import type { ReceiptRow } from '../../db/types';
import { plural } from '../shop/status';
import { cardCls, sectionTitleCls } from '../ui/classes';

const TOP_SHOWN = 8;
const whole = (gr: number) => formatGr(Math.round(gr / 100) * 100).replace(',00', '');

/** Wydatki w Lidlu: słupki 12 miesięcy, wybrany miesiąc w liczbach i na co poszły pieniądze. */
export function SpendingSection({ receipts }: { receipts: readonly ReceiptRow[] }) {
  const months = monthlySpend(receipts, monthsBack(12));
  const lastWith = [...months].reverse().find((m) => m.receipts > 0)?.key ?? months.at(-1)!.key;
  const [selected, setSelected] = useState<string | null>(null);
  const key = selected ?? lastWith;
  const i = months.findIndex((m) => m.key === key);
  const month = months[i]!;
  const prev = i > 0 ? months[i - 1]! : null;
  const active = months.filter((m) => m.receipts > 0);
  const avg = active.length > 0 ? active.reduce((s, m) => s + m.totalGr, 0) / active.length : 0;

  return (
    <section className="flex flex-col gap-2">
      <h2 className={sectionTitleCls}>Wydatki w Lidlu</h2>
      <div className={`${cardCls} flex flex-col gap-4 p-4`}>
        <Bars months={months} selected={key} onSelect={setSelected} />
        <div>
          <p className="text-sm text-stone-500">{monthLabel(key)}</p>
          <p className="text-3xl font-bold tabular-nums">{formatGr(month.totalGr)}</p>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            {month.receipts} {plural(month.receipts, 'paragon', 'paragony', 'paragonów')}
            {month.savingsGr > 0 && <span className="text-brand-700 dark:text-brand-500"> · oszczędności {formatGr(month.savingsGr)}</span>}
          </p>
          <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
            {prev && prev.totalGr > 0 && <Change label={`vs ${monthLabel(prev.key, 'short').toLowerCase()}`} now={month.totalGr} before={prev.totalGr} />}
            {avg > 0 && active.length > 1 && <Change label="vs średnia roku" now={month.totalGr} before={avg} />}
          </p>
        </div>
        <MonthItems monthKey={month.receipts > 0 ? key : null} total={month.totalGr} />
        <p className="text-xs text-stone-500">Tylko zakupy z e-paragonów Lidl Plus — Kaufland i inne sklepy tu nie trafiają.</p>
      </div>
    </section>
  );
}

function Bars({ months, selected, onSelect }: { months: MonthSpend[]; selected: string; onSelect: (k: string) => void }) {
  const max = Math.max(...months.map((m) => m.totalGr), 1);
  return (
    <div className="flex h-28 items-end gap-1" role="group" aria-label="Wydatki w ostatnich 12 miesiącach">
      {months.map((m) => {
        const on = m.key === selected;
        return (
          <button
            key={m.key}
            type="button"
            onClick={() => onSelect(m.key)}
            aria-pressed={on}
            aria-label={`${monthLabel(m.key)}: ${formatGr(m.totalGr)}`}
            className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
          >
            <span
              className={`w-full rounded-t ${on ? 'bg-brand-600' : 'bg-stone-300 dark:bg-stone-700'}`}
              style={{ height: `${Math.max(2, (m.totalGr / max) * 84)}px` }}
            />
            <span className={`text-[10px] leading-none ${on ? 'font-semibold text-stone-800 dark:text-stone-100' : 'text-stone-500'}`}>
              {monthLabel(m.key, 'short').slice(0, 3)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Change({ label, now, before }: { label: string; now: number; before: number }) {
  const pct = Math.round(((now - before) / before) * 100);
  const up = pct > 0;
  return (
    <span className="mr-3 inline-block">
      <span className={up ? 'text-miss-600' : 'text-brand-700 dark:text-brand-500'}>
        {up ? '▲' : pct < 0 ? '▼' : '='} {Math.abs(pct)}%
      </span>{' '}
      {label} ({whole(before)})
    </span>
  );
}

function MonthItems({ monthKey, total }: { monthKey: string | null; total: number }) {
  const items = useMonthItems(monthKey);
  const [all, setAll] = useState(false);
  if (!items || items.length === 0) return null;
  const shown = all ? items : items.slice(0, TOP_SHOWN);
  const max = items[0]!.totalGr || 1;
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs font-semibold tracking-wide text-stone-500 uppercase">Najwięcej wydane na</p>
      <ul className="flex flex-col gap-1.5">
        {shown.map((it) => (
          <li key={`${it.kind}:${it.id}`}>
            <Link to={`/${it.kind === 'product' ? 'products' : 'articles'}/${encodeURIComponent(it.id)}`} className="block">
              <div className="flex items-baseline gap-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {it.name}
                  {it.count > 1 && <span className="text-stone-500"> · {it.count}×</span>}
                </span>
                <span className="shrink-0 tabular-nums">{formatGr(it.totalGr)}</span>
              </div>
              <div className="mt-0.5 h-1 rounded-full bg-stone-100 dark:bg-stone-800">
                <div className="h-full rounded-full bg-brand-500" style={{ width: `${(it.totalGr / max) * 100}%` }} />
              </div>
            </Link>
          </li>
        ))}
      </ul>
      {!all && items.length > TOP_SHOWN && (
        <button type="button" onClick={() => setAll(true)} className="min-h-10 self-start text-sm font-medium text-brand-700 dark:text-brand-500">
          Pokaż wszystko ({items.length} pozycji, {formatGr(total)})
        </button>
      )}
    </div>
  );
}
